import { InventoryReportSnapshotItemRow } from './types';

/** Convert to boolean - handles string "0"/"1", number 0/1, null, undefined correctly */
function toBool(v: any): boolean {
    return v === true || v === 1 || v === '1' || v === 'true';
}

/** Pre-computed lookup for validation errors by item_id - avoids O(n*m) find() inside map */
export interface ValidationLookup {
    byItemId: Map<number, { errors?: string[]; warnings?: string[] }>;
    itemIdsWithWarningsSet: Set<number>;
}

export function buildValidationLookup(
    validationErrorsList: Array<{ item_id: number; errors?: string[]; warnings?: string[] }>,
    itemIdsWithWarnings: number[]
): ValidationLookup {
    const byItemId = new Map<number, { errors?: string[]; warnings?: string[] }>();
    for (const err of validationErrorsList) {
        if (err.item_id != null) {
            byItemId.set(err.item_id, { errors: err.errors, warnings: err.warnings });
        }
    }
    const itemIdsWithWarningsSet = new Set(itemIdsWithWarnings);
    return { byItemId, itemIdsWithWarningsSet };
}

const FIELD_MAP: { [key: string]: string } = {
    description: 'description',
    onHandQuantity: 'on_hand_quantity',
    reorderLevel: 'reorder_level',
    maxOrderLevel: 'max_order_level',
    orderQtyToPlace: 'order_qty_to_place',
    quantityToOrder: 'quantity_to_order',
    supplier: 'supplier_name',
    location: 'location_name',
};

export const SORTABLE_FIELDS = ['description', 'supplier'] as const;

export function buildOrderByParam(orderByField?: string, orderDirection?: string): string | null {
    if (!orderByField || !SORTABLE_FIELDS.includes(orderByField as typeof SORTABLE_FIELDS[number])) {
        return null;
    }
    const field = FIELD_MAP[orderByField] || orderByField;
    return orderDirection === 'desc' ? `-${field}` : field;
}

export function mapSnapshotItemToRow(
    item: any,
    options: {
        validationErrorsList: Array<{ item_id: number; errors?: string[]; warnings?: string[] }>;
        itemIdsWithWarnings: number[];
        validationLookup?: ValidationLookup;
        unsavedChanges: Record<string, { newValue: number }>;
    }
): InventoryReportSnapshotItemRow {
    const onHandQty = item.on_hand_quantity || item.onHandQuantity || 0;
    const maxOrder = item.max_order_level || item.maxOrderLevel || 0;
    const snapshotItemId = item.id || item.snapshot_item_id || item.snapshotItemId || item.item_id;
    const rowId = `${item.product_uuid || item.productUuid}_${item.location_id || item.locationId}`;

    const itemErrors = options.validationLookup
        ? options.validationLookup.byItemId.get(Number(snapshotItemId))
        : options.validationErrorsList.find((err) => err.item_id === snapshotItemId);
    const hasWarnings = options.validationLookup
        ? options.validationLookup.itemIdsWithWarningsSet.has(Number(snapshotItemId))
        : options.itemIdsWithWarnings.includes(Number(snapshotItemId));
    const validationErrorsForRow = itemErrors
        ? (itemErrors.warnings || itemErrors.errors)
        : hasWarnings
        ? ['See validation warnings panel for details']
        : undefined;

    const supp = item.supplier || item.supplier_name || '';
    const hasNoSupplier = !(supp && String(supp).trim());
    const baseOrderQty = Math.max(
        0,
        item.order_qty_to_place !== undefined
            ? Number(item.order_qty_to_place)
            : item.order !== undefined
            ? Number(item.order)
            : Math.max(0, maxOrder - onHandQty)
    );
    const uc = options.unsavedChanges[rowId];
    const displayOrderQty = uc ? uc.newValue : baseOrderQty;

    return {
        id: rowId,
        description: item.description || item.article_name || '',
        supplier: supp,
        supplierUuid: item.supplier_uuid || item.supplierUuid || '',
        noSupplier: hasNoSupplier,
        location: item.location || item.location_name || '',
        onHandQuantity: onHandQty,
        reorderLevel: item.reorder_level || item.reorderLevel || 0,
        maxOrderLevel: maxOrder,
        orderQtyToPlace: displayOrderQty,
        shortStock: toBool(item.short_stock) || toBool(item.shortStock),
        anyShort: toBool(item.any_short) || toBool(item.anyShort),
        receivedLastTime: item.received_last_time || item.receivedLastTime || 0,
        receivedLastDate: item.received_last_date || item.receivedLastDate || null,
        sales30Days: item.sales_30_days || item.sales30Days || 0,
        sales60Days: item.sales_60_days || item.sales60Days || 0,
        sales90Days: item.sales_90_days || item.sales90Days || 0,
        sales180Days: item.sales_180_days || item.sales180Days || 0,
        lastPurchasePrice: item.last_purchase_price || item.lastPurchasePrice || 0,
        retailPrice: item.retail_price || item.retailPrice || 0,
        profitMargin: item.profit_margin || item.profitMargin || '0%',
        quantityInCase: item.quantity_in_case || item.quantityInCase || 0,
        onOrder: item.on_order || item.onOrder || 0,
        quantityToOrder: item.quantity_to_order || item.quantityToOrder || 0,
        productUuid: item.product_uuid || item.productUuid || '',
        locationId: item.location_id || item.locationId || 0,
        snapshotItemId: snapshotItemId ? Number(snapshotItemId) : undefined,
        validationErrors: validationErrorsForRow,
    };
}

export function mapGetInventoryReportToRow(item: any): InventoryReportSnapshotItemRow {
    const onHandQty = item.on_hand_quantity || item.quantity_in_hand || 0;
    const maxOrder = item.max_order_level || 0;
    return {
        id: `${item.product_uuid}_${item.location_id}`,
        description: item.article_name || item.description || '',
        supplier: item.supplier || item.supplier_name || '',
        supplierUuid: item.supplier_uuid || item.supplierUuid || '',
        location: item.location || item.location_name || '',
        onHandQuantity: onHandQty,
        reorderLevel: item.reorder_level || 0,
        maxOrderLevel: maxOrder,
        orderQtyToPlace: Math.max(
            0,
            item.order_qty_to_place !== undefined
                ? Number(item.order_qty_to_place)
                : item.order !== undefined
                ? Number(item.order)
                : Math.max(0, maxOrder - onHandQty)
        ),
        shortStock: toBool(item.short_stock),
        anyShort: toBool(item.any_short),
        receivedLastTime: item.received_last_time || 0,
        receivedLastDate: item.received_last_date || null,
        sales30Days: item.sales_30_days || 0,
        sales60Days: item.sales_60_days || 0,
        sales90Days: item.sales_90_days || 0,
        sales180Days: item.sales_180_days || 0,
        lastPurchasePrice: item.last_purchase_price || 0,
        retailPrice: item.retail_price || 0,
        profitMargin: item.profit_margin || '0%',
        quantityInCase: item.quantity_in_case || 0,
        onOrder: item.on_order || 0,
        quantityToOrder: item.quantity_to_order || 0,
        productUuid: item.product_uuid || '',
        locationId: item.location_id || 0,
    };
}
